import React from 'react';
import { Head, useForm } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { ArrowLeft } from 'lucide-react';
import FilterSelector from '../../../components/FilterSelector';
import {
  PRACTICE_LOCATIONS,
  PARKING_TYPES,
  PREFERRED_CONTACTS,
  APPOINTMENT_METHODS,
  NEW_PATIENTS_POLICIES,
  SESSION_DURATIONS,
  PATIENT_TYPES,
  PRACTICE_MODES,
  PAYMENT_METHODS,
  COORDINATED_PRACTICE_TYPES,
  SPECIALTIES,
  TECHNIQUES,
  EQUIPMENT,
  ACCESSIBILITY,
  LANGUAGES,
  SPORTS,
  OTHER_ACTIVITIES,
} from '../../../lib/constants';

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  gender?: string;
  address?: string;
  city?: string;
  zip?: string;
  phone?: string;
  profile_type?: string;
  profile_score?: number;
  appointment_mode?: string;
  payment_type?: string;
  booking_link?: string;
  profile_complete?: boolean;
  certified?: boolean;
  specialties?: string[];
  body_zones?: string[];
  exercise_types?: string[];
  accessibilities?: string[];
  languages?: string[];
  equipment?: string[];
  sports?: string[];
  // Nouveaux champs
  practice_locations?: string[];
  home_visit_areas?: string;
  professional_address?: string;
  secondary_address?: string;
  practice_name?: string;
  practitioners_count?: number;
  parking_types?: string[];
  professional_email?: string;
  professional_website?: string;
  preferred_contact?: string;
  preferred_contact_details?: string;
  appointment_methods?: string[];
  online_booking_link?: string;
  secretariat_hours?: string;
  new_patients_policy?: string;
  session_duration?: string;
  patient_types?: string[];
  waiting_list?: boolean;
  waiting_list_delay?: string;
  practice_mode?: string;
  practice_modes?: string[];
  honorary_details?: string;
  unconventional_practice_description?: string;
  payment_methods?: string[];
  coordinated_practice?: string[];
  coordinated_practice_details?: string;
  techniques?: string[];
  other_techniques?: string;
  training_courses?: string;
  cv_url?: string;
  diploma_year?: number;
  diploma_place?: string;
  diploma_extra_1?: string;
  diploma_extra_2?: string;
  other_activities?: string[];
  other_activities_details?: string;
  association_name?: string;
  rpps_number?: string;
  adeli_number?: string;
  publications?: string;
  other_equipment?: string;
  other_languages?: string;
  other_sports?: string;
}

interface FilterValue { id: number; value: string; active: boolean; position: number; physiotherapists_count?: number; }
interface FilterCategory { id: number; name: string; key: string; description: string | null; active: boolean; position: number; filter_values: FilterValue[]; }
interface Props { physiotherapist: Physiotherapist; filter_categories?: FilterCategory[]; selected_filter_values?: number[]; errors?: string[]; }

const EditPhysiotherapist: React.FC<Props> = ({ physiotherapist, filter_categories = [], selected_filter_values = [], errors = [] }) => {
  const [selectedFilters, setSelectedFilters] = React.useState<number[]>(selected_filter_values);
  React.useEffect(() => { setSelectedFilters(selected_filter_values); }, [selected_filter_values]);

  const { data, setData, put, processing } = useForm({
    first_name: physiotherapist.first_name || '',
    last_name: physiotherapist.last_name || '',
    gender: physiotherapist.gender || '',
    address: physiotherapist.address || '',
    city: physiotherapist.city || '',
    zip: physiotherapist.zip || '',
    phone: physiotherapist.phone || '',
    profile_type: physiotherapist.profile_type || '',
    profile_score: physiotherapist.profile_score || 0,
    appointment_mode: physiotherapist.appointment_mode || '',
    payment_type: physiotherapist.payment_type || '',
    booking_link: physiotherapist.booking_link || '',
    profile_complete: physiotherapist.profile_complete || false,
    certified: physiotherapist.certified || false,
    specialties: physiotherapist.specialties || [],
    body_zones: physiotherapist.body_zones || [],
    exercise_types: physiotherapist.exercise_types || [],
    accessibilities: physiotherapist.accessibilities || [],
    languages: physiotherapist.languages || [],
    equipment: physiotherapist.equipment || [],
    sports: physiotherapist.sports || [],
    // Nouveaux champs
    practice_locations: physiotherapist.practice_locations || [],
    home_visit_areas: physiotherapist.home_visit_areas || '',
    professional_address: physiotherapist.professional_address || '',
    secondary_address: physiotherapist.secondary_address || '',
    practice_name: physiotherapist.practice_name || '',
    practitioners_count: physiotherapist.practitioners_count || 1,
    parking_types: physiotherapist.parking_types || [],
    professional_email: physiotherapist.professional_email || '',
    professional_website: physiotherapist.professional_website || '',
    preferred_contact: physiotherapist.preferred_contact || '',
    preferred_contact_details: physiotherapist.preferred_contact_details || '',
    appointment_methods: physiotherapist.appointment_methods || [],
    online_booking_link: physiotherapist.online_booking_link || '',
    secretariat_hours: physiotherapist.secretariat_hours || '',
    new_patients_policy: physiotherapist.new_patients_policy || '',
    session_duration: physiotherapist.session_duration || '',
    patient_types: physiotherapist.patient_types || [],
    waiting_list: physiotherapist.waiting_list || false,
    waiting_list_delay: physiotherapist.waiting_list_delay || '',
    practice_mode: physiotherapist.practice_mode || '',
    practice_modes: physiotherapist.practice_modes || [],
    honorary_details: physiotherapist.honorary_details || '',
    unconventional_practice_description: physiotherapist.unconventional_practice_description || '',
    payment_methods: physiotherapist.payment_methods || [],
    coordinated_practice: physiotherapist.coordinated_practice || [],
    coordinated_practice_details: physiotherapist.coordinated_practice_details || '',
    techniques: physiotherapist.techniques || [],
    other_techniques: physiotherapist.other_techniques || '',
    training_courses: physiotherapist.training_courses || '',
    cv_url: physiotherapist.cv_url || '',
    diploma_year: physiotherapist.diploma_year || undefined,
    diploma_place: physiotherapist.diploma_place || '',
    diploma_extra_1: physiotherapist.diploma_extra_1 || '',
    diploma_extra_2: physiotherapist.diploma_extra_2 || '',
    other_activities: physiotherapist.other_activities || [],
    other_activities_details: physiotherapist.other_activities_details || '',
    association_name: physiotherapist.association_name || '',
    rpps_number: physiotherapist.rpps_number || '',
    adeli_number: physiotherapist.adeli_number || '',
    publications: physiotherapist.publications || '',
    other_equipment: physiotherapist.other_equipment || '',
    other_languages: physiotherapist.other_languages || '',
    other_sports: physiotherapist.other_sports || '',
    filter_value_ids: selectedFilters
  });

  React.useEffect(() => { setData('filter_value_ids', selectedFilters); }, [selectedFilters, setData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setData('filter_value_ids', selectedFilters);
    setTimeout(() => { put(`/admin/physiotherapists/${physiotherapist.id}`); }, 0);
  };

  const handleArrayChange = (field: keyof typeof data, value: string, checked: boolean) => {
    const currentArray = data[field] as string[] || [];
    setData(field, checked ? [...currentArray, value] : currentArray.filter(item => item !== value));
  };

  const removeArrayItem = (field: keyof typeof data, value: string) => {
    const currentArray = data[field] as string[] || [];
    setData(field, currentArray.filter(item => item !== value));
  };

  const renderArrayField = (field: keyof typeof data, options: readonly string[], label: string, description?: string) => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {description && (<p className="text-sm text-gray-500 mb-3">{description}</p>)}
      </div>
      {(data[field] as string[])?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {(data[field] as string[]).map((item) => (
            <Badge key={item} variant="secondary" className="flex items-center gap-1">
              {item}
              <button type="button" onClick={() => removeArrayItem(field, item)} className="ml-1 hover:text-red-600">×</button>
            </Badge>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {options.map((option) => (
          <label key={option} className="flex items-center space-x-2">
            <Checkbox
              checked={(data[field] as string[])?.includes(option)}
              onCheckedChange={(checked) => handleArrayChange(field, option, checked as boolean)}
            />
            <span className="text-sm text-gray-700">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderMultiSelectDropdown = (
    field: keyof typeof data,
    options: readonly string[],
    label: string,
    placeholder = 'Sélectionner...'
  ) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" className="w-full justify-between">
            <span className="truncate">{(data[field] as string[])?.length > 0 ? (data[field] as string[]).join(', ') : placeholder}</span>
            <span className="opacity-60">▼</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-md">
          {options.map((option) => {
            const checked = (data[field] as string[])?.includes(option);
            return (
              <DropdownMenuItem key={option} className="flex items-center space-x-2" onClick={(e) => { e.preventDefault(); handleArrayChange(field, option, !checked); }}>
                <Checkbox checked={checked} onCheckedChange={(c) => handleArrayChange(field, option, c as boolean)} />
                <span>{option}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <AdminLayout title={`Modifier ${physiotherapist.first_name} ${physiotherapist.last_name}`}>
      <Head title={`Modifier ${physiotherapist.first_name} ${physiotherapist.last_name} - Admin`} />

      <div className="space-y-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Modifier {physiotherapist.first_name} {physiotherapist.last_name}</h1>
              <p className="text-gray-600">Kinésithérapeute #{physiotherapist.id}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-10">
          {errors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <ul className="text-sm text-red-600 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Informations de base */}
          <Card>
            <CardHeader>
              <CardTitle>Informations de base</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                  <Input value={data.first_name} onChange={(e) => setData('first_name', e.target.value)} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                  <Input value={data.last_name} onChange={(e) => setData('last_name', e.target.value)} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Genre</label>
                  <Select value={data.gender} onValueChange={(value) => setData('gender', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un genre" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Homme</SelectItem>
                      <SelectItem value="F">Femme</SelectItem>
                      <SelectItem value="O">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <Input value={data.phone} onChange={(e) => setData('phone', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">N° RPPS</label>
                  <Input value={data.rpps_number} onChange={(e) => setData('rpps_number', e.target.value)} placeholder="Ex: 10001234567" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">N° ADELI</label>
                  <Input value={data.adeli_number} onChange={(e) => setData('adeli_number', e.target.value)} placeholder="Ex: 751234567" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Année du diplôme</label>
                  <Input type="number" value={data.diploma_year as number | undefined as any} onChange={(e) => setData('diploma_year', parseInt(e.target.value) || undefined)} placeholder="Ex: 2015" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lieu du diplôme</label>
                  <Input value={data.diploma_place} onChange={(e) => setData('diploma_place', e.target.value)} placeholder="Ex: Paris" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Adresse */}
          <Card>
            <CardHeader>
              <CardTitle>Adresse</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                  <Input value={data.address} onChange={(e) => setData('address', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                  <Input value={data.city} onChange={(e) => setData('city', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                  <Input value={data.zip} onChange={(e) => setData('zip', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cabinet et pratique */}
          <Card>
            <CardHeader>
              <CardTitle>Cabinet et pratique</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du cabinet</label>
                  <Input value={data.practice_name} onChange={(e) => setData('practice_name', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse secondaire (le cas échéant)</label>
                  <Input value={data.secondary_address} onChange={(e) => setData('secondary_address', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de praticiens</label>
                  <Input type="number" min="1" value={data.practitioners_count} onChange={(e) => setData('practitioners_count', parseInt(e.target.value) || 1)} />
                </div>
                <div className="md:col-span-2">
                  {renderArrayField('parking_types', PARKING_TYPES, 'Type de parking (plusieurs possibles)')}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zones de visite à domicile</label>
                  <Textarea value={data.home_visit_areas} onChange={(e) => setData('home_visit_areas', e.target.value)} placeholder="Ex: Paris 1er, 2ème, 3ème arrondissements" />
                </div>
              </div>
              {renderMultiSelectDropdown('practice_locations', PRACTICE_LOCATIONS, 'Lieux de pratique', 'Sélectionner les lieux')}
              {renderArrayField('accessibilities', ACCESSIBILITY, 'Accessibilité')}
            </CardContent>
          </Card>

          {/* Contact et communication */}
          <Card>
            <CardHeader>
              <CardTitle>Contact et communication</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Prise de rendez-vous en premier */}
              <div className="mb-6">
                {renderMultiSelectDropdown('appointment_methods', APPOINTMENT_METHODS, 'Modes de prise de rendez-vous')}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lien de réservation en ligne</label>
                  <Input type="url" value={data.online_booking_link} onChange={(e) => setData('online_booking_link', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email professionnel</label>
                  <Input type="email" value={data.professional_email} onChange={(e) => setData('professional_email', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site web professionnel</label>
                  <Input type="url" value={data.professional_website} onChange={(e) => setData('professional_website', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact préférentiel</label>
                  <Select value={data.preferred_contact} onValueChange={(value) => setData('preferred_contact', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un contact préférentiel" />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFERRED_CONTACTS.map((contact) => (
                        <SelectItem key={contact} value={contact}>{contact}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Détails du contact préférentiel</label>
                  <Input value={data.preferred_contact_details} onChange={(e) => setData('preferred_contact_details', e.target.value)} placeholder="Ex: Laissez un message et nous vous rappellerons, ou les mails sont traités sous 48h" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Informations complémentaires (horaires secrétariat,…)</label>
                  <Textarea value={data.secretariat_hours} onChange={(e) => setData('secretariat_hours', e.target.value)} placeholder="Ex: Lundi-Vendredi 9h-18h" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Patients et séances */}
          <Card>
            <CardHeader>
              <CardTitle>Patients et séances</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prise en charge nouveaux patients</label>
                  <Select value={data.new_patients_policy} onValueChange={(value) => setData('new_patients_policy', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une politique" />
                    </SelectTrigger>
                    <SelectContent>
                      {NEW_PATIENTS_POLICIES.map((policy) => (
                        <SelectItem key={policy} value={policy}>{policy}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durée des séances</label>
                  <Select value={data.session_duration} onValueChange={(value) => setData('session_duration', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une durée" />
                    </SelectTrigger>
                    <SelectContent>
                      {SESSION_DURATIONS.map((duration) => (
                        <SelectItem key={duration} value={duration}>{duration}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="waiting_list" checked={data.waiting_list} onCheckedChange={(checked) => setData('waiting_list', checked as boolean)} />
                  <label htmlFor="waiting_list" className="text-sm font-medium text-gray-700">Liste d'attente</label>
                </div>
                {data.waiting_list && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Délai d'attente</label>
                    <Input value={data.waiting_list_delay} onChange={(e) => setData('waiting_list_delay', e.target.value)} placeholder="Ex: 2-3 semaines" />
                  </div>
                )}
              </div>
              {renderArrayField('patient_types', PATIENT_TYPES, 'Types de patients pris en charge')}
            </CardContent>
          </Card>

          {/* Spécialités et techniques */}
          <Card>
            <CardHeader>
              <CardTitle>Spécialités et techniques</CardTitle>
            </CardHeader>
            <CardContent>
              {renderArrayField('specialties', SPECIALTIES, 'Spécialités')}
              <div className="mt-6">
                {renderArrayField('techniques', TECHNIQUES, 'Techniques utilisées')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Autres techniques</label>
                <Textarea value={data.other_techniques} onChange={(e) => setData('other_techniques', e.target.value)} placeholder="Précisez d'autres techniques non listées" />
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Formations suivies</label>
                <Textarea value={data.training_courses} onChange={(e) => setData('training_courses', e.target.value)} placeholder="Listez les formations suivies..." rows={4} />
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">CV</label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setData('cv_url', file as any);
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Équipements */}
          <Card>
            <CardHeader>
              <CardTitle>Équipements</CardTitle>
            </CardHeader>
            <CardContent>
              {renderArrayField('equipment', EQUIPMENT, 'Équipements disponibles')}
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Autres équipements</label>
                <Input value={data.other_equipment} onChange={(e) => setData('other_equipment', e.target.value)} placeholder="Précisez d'autres équipements non listés" />
              </div>
            </CardContent>
          </Card>

          {/* Sports et Langues */}
          <Card>
            <CardHeader>
              <CardTitle>Sports et Langues</CardTitle>
            </CardHeader>
            <CardContent>
              {renderArrayField('sports', SPORTS, 'Sports spécialisés')}
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Autres sports</label>
                <Input value={data.other_sports} onChange={(e) => setData('other_sports', e.target.value)} placeholder="Précisez d'autres sports non listés" />
              </div>

              <div className="mt-6">
                {renderArrayField('languages', LANGUAGES, 'Langues parlées')}
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Autres langues</label>
                <Input value={data.other_languages} onChange={(e) => setData('other_languages', e.target.value)} placeholder="Précisez d'autres langues non listées" />
              </div>
            </CardContent>
          </Card>

          {/* Autres activités */}
          <Card>
            <CardHeader>
              <CardTitle>Autres activités</CardTitle>
            </CardHeader>
            <CardContent>
              {renderArrayField('other_activities', OTHER_ACTIVITIES, 'Autres activités proposées')}
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Précisez</label>
                <Input value={data.other_activities_details} onChange={(e) => setData('other_activities_details', e.target.value)} placeholder="Précisez d'autres activités" />
              </div>
            </CardContent>
          </Card>

          {/* Mode d'exercice et règlement */}
          <Card>
            <CardHeader>
              <CardTitle>Mode d'exercice et règlement</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Mode d'exercice (checkboxes only) */}
              {renderArrayField('practice_modes', PRACTICE_MODES, "Mode d'exercice")}
              
              <div className="mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dépassement d'honoraires: préciser</label>
                  <Textarea value={data.honorary_details} onChange={(e) => setData('honorary_details', e.target.value)} placeholder="Précisez les conditions DE/HN" />
                </div>
              </div>

              {data.practice_modes?.includes("Activité déconventionnée") && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pratique déconventionnée : description</label>
                  <Textarea value={data.unconventional_practice_description} onChange={(e) => setData('unconventional_practice_description', e.target.value)} placeholder="Décrivez l'activité déconventionnée, tarifs, prestations..." rows={4} />
                </div>
              )}

              <div className="mt-6">
                {renderArrayField('payment_methods', PAYMENT_METHODS, 'Type de règlement')}
              </div>

              <div className="mt-6">
                {renderArrayField('coordinated_practice', COORDINATED_PRACTICE_TYPES, 'Exercice coordonné')}
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Précisions</label>
                  <Textarea value={data.coordinated_practice_details} onChange={(e) => setData('coordinated_practice_details', e.target.value)} placeholder="Précisions sur la CPTS/MSP/Autres" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Associations</label>
                  <Textarea value={data.association_name} onChange={(e) => setData('association_name', e.target.value)} placeholder="Ex: Membre de la SFMKS, ..." rows={2} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Publications</label>
                  <Textarea value={data.publications} onChange={(e) => setData('publications', e.target.value)} placeholder="Listez vos publications..." rows={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statuts */}
          <Card>
            <CardHeader>
              <CardTitle>Statuts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-6">
                <div className="flex items-center space-x-2">
                  <Checkbox id="certified" checked={data.certified} onCheckedChange={(checked) => setData('certified', checked as boolean)} />
                  <label htmlFor="certified" className="text-sm font-medium text-gray-700">Certifié</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="profile_complete" checked={data.profile_complete} onCheckedChange={(checked) => setData('profile_complete', checked as boolean)} />
                  <label htmlFor="profile_complete" className="text-sm font-medium text-gray-700">Profil complet</label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sélecteur de filtres dynamiques */}
          {filter_categories.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <FilterSelector categories={filter_categories} selectedValues={selectedFilters} onSelectionChange={setSelectedFilters} title="Filtres du kinésithérapeute" />
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => window.history.back()}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Modification...' : 'Modifier le kinésithérapeute'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default EditPhysiotherapist; 
